// cameraRig.ts
import type * as THREE_NS from 'three';

// Re-use your existing enums (string values must match state.ts)
export enum NavCameraMotion {
  ToLogin = 'to-login',
  ToSettings = 'to-settings',
  ToHome = 'to-home',
}

export enum IdleCameraMotion {
  CircularOrbit = 'circular-orbit',
  PendulumOrbit = 'pendulum-orbit',
  Lissajous = 'lissajous',
  Helix = 'helix',
  GoldenRatioOrbit = 'golden-ratio-orbit',
  MicroDollyBreathing = 'micro-dolly-breathing',
  DebugRotation = 'debug-rotation',
  OrganicJostle = 'organic-jostle',
  Static = 'static',
  BirdsEye = 'birds-eye',
}

// Optional: intro variants (same labels you had before)
export type IntroEffectType =
  | 'dolly-zoom'
  | 'fov-ramp'
  | 'push-in-fov-expansion'
  | 'barrel-to-pincushion'
  | 'dive-up'
  | 'dive-down';

export interface CameraRigOptions {
  THREE: typeof THREE_NS;
  camera: THREE_NS.PerspectiveCamera;
  // Read current idle mode from app state each frame
  getIdleMode: () => IdleCameraMotion;
  // Mouse in world-ish units for lookAt steering
  getMouseLookOffset: () => { x: number; y: number };
}

export interface CameraRig {
  update(nowSeconds: number): { distortionK: number };
  startIntro(effect: IntroEffectType): void;
  triggerNav(motion: NavCameraMotion): void;
  flyTo(target: { pos?: THREE_NS.Vector3; lookAt?: THREE_NS.Vector3; fov?: number }, duration: number): void;
  getCurrentIntroType(): IntroEffectType | null;
}

interface CameraState {
  pos: THREE_NS.Vector3;
  target: THREE_NS.Vector3;
  fov: number;
  distortionK: number;
  up: THREE_NS.Vector3;
}

// Utility
const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const smoothstep = (t: number) => t * t * (3 - 2 * t);
const toRad = (deg: number) => (deg * Math.PI) / 180;

const BASE_FOV = 80; // Increased from 75 for wider field of view
const FOV_BREATH_AMPL = 8;
const FOV_BREATH_PERIOD = 60; // s
const ORBIT_RADIUS = 28; // Reduced from 28 to bring camera closer to metaball (makes it appear larger)

// spring-ish smoothing
const FOV_LERP = 0.12;
const POS_LERP = 0.08;
const TGT_LERP = 0.12;
const DISTORT_LERP = 0.1;
const UP_LERP = 0.1;

// noise
const NOISE_POS_AMPL = 2.5;
const NOISE_POS_LERP = 0.003;

// intro + nav durations
const INTRO_DURATION = 3.5;
const NAV_DURATION = 1.0;

export function createCameraRig(opts: CameraRigOptions): CameraRig {
  const { THREE, camera, getIdleMode, getMouseLookOffset } = opts;

  // --- internal time baselines ---
  let idleStart = 0;
  let introStart: number | null = null;
  let introType: IntroEffectType | null = null;
  let navStart: number | null = null;
  let navType: NavCameraMotion | null = null;
  let heldNavState: CameraState | null = null; // Track held state for ToLogin/ToSettings

  // --- flight state ---
  let flightStart: number | null = null;
  let flightDuration: number = 0;
  let flightStartState: CameraState | null = null;
  let flightTargetState: Partial<CameraState> | null = null;

  // --- smoothing state ---
  let smoothed: CameraState = {
    pos: camera.position.clone(),
    target: new THREE.Vector3(0, 0, 0),
    fov: BASE_FOV,
    distortionK: 0,
    up: camera.up.clone(),
  };

  // noise state
  let noisePos = new THREE.Vector3();
  let noiseTarget = new THREE.Vector3();

  function evalIdleBase(now: number): CameraState {
    const idleT = now - idleStart;
    const mode = getIdleMode();
    const breathPhase = (now * 2 * Math.PI) / FOV_BREATH_PERIOD;
    let fov = BASE_FOV + Math.sin(breathPhase) * FOV_BREATH_AMPL;

    const angleBase = idleT * 0.02; // global "orbital phase"
    let x = 0, y = 0, z = 0;
    let up = new THREE.Vector3(0, 1, 0);

    switch (mode) {
      case IdleCameraMotion.CircularOrbit: {
        x = Math.cos(angleBase) * ORBIT_RADIUS;
        z = Math.sin(angleBase) * ORBIT_RADIUS;
        y = 0;
        break;
      }
      case IdleCameraMotion.PendulumOrbit: {
        const swing = 0.18 * Math.sin(idleT * 0.08);
        const theta = angleBase + swing;
        x = Math.cos(theta) * ORBIT_RADIUS;
        z = Math.sin(theta) * ORBIT_RADIUS;
        y = Math.sin(idleT * 0.04) * 0.8;
        break;
      }
      case IdleCameraMotion.Lissajous: {
        x = Math.sin(idleT * 0.05) * ORBIT_RADIUS;
        z = Math.sin(idleT * 0.07) * ORBIT_RADIUS;
        y = Math.sin(idleT * 0.03) * 0.8;
        break;
      }
      case IdleCameraMotion.Helix: {
        const a = idleT * 0.015;
        x = Math.cos(a) * ORBIT_RADIUS;
        z = Math.sin(a) * ORBIT_RADIUS;
        y = Math.sin(idleT * 0.03) * 0.8;
        break;
      }
      case IdleCameraMotion.GoldenRatioOrbit: {
        const phi = 1.6180339887;
        const t = idleT * 0.04;
        x = Math.cos(t) * ORBIT_RADIUS;
        z = Math.sin(t * phi) * ORBIT_RADIUS;
        y = Math.sin(idleT * 0.025) * 0.8;
        break;
      }
      case IdleCameraMotion.DebugRotation: {
        // Fast orbit for debugging - quick rotation to see the whole scene
        const fastAngle = idleT * 0.3; // Much faster than normal (0.02 -> 0.3)
        x = Math.cos(fastAngle) * ORBIT_RADIUS;
        z = Math.sin(fastAngle) * ORBIT_RADIUS;
        y = Math.sin(idleT * 0.1) * 2; // Slight vertical movement
        break;
      }
      case IdleCameraMotion.OrganicJostle: {
        // Stay roughly at the same position with subtle, organic jostling
        // Use multiple sine waves at different frequencies and phases for natural movement
        const baseX = ORBIT_RADIUS;
        const baseZ = 0;
        const baseY = 0;
        
        // Multiple frequencies create organic, non-repetitive motion
        const jostleX = Math.sin(idleT * 0.015) * 1.2 + 
                       Math.sin(idleT * 0.023) * 0.8 + 
                       Math.sin(idleT * 0.031) * 0.5;
        const jostleZ = Math.sin(idleT * 0.019) * 1.0 + 
                       Math.sin(idleT * 0.027) * 0.7 + 
                       Math.cos(idleT * 0.035) * 0.4;
        const jostleY = Math.sin(idleT * 0.011) * 0.6 + 
                       Math.cos(idleT * 0.017) * 0.4;
        
        x = baseX + jostleX;
        z = baseZ + jostleZ;
        y = baseY + jostleY;
        break;
      }
      case IdleCameraMotion.Static: {
        x = ORBIT_RADIUS;
        z = 0;
        y = 0;
        fov = BASE_FOV; // No breathing
        break;
      }
      case IdleCameraMotion.BirdsEye: {
        // Same jostle behavior as OrganicJostle but looking down from above
        const baseX = 0;
        const baseZ = 0;
        const baseY = ORBIT_RADIUS; // Look down from above
        
        // Reusing the jostle math
        const jostleX = Math.sin(idleT * 0.015) * 1.2 + 
                       Math.sin(idleT * 0.023) * 0.8 + 
                       Math.sin(idleT * 0.031) * 0.5;
        const jostleZ = Math.sin(idleT * 0.019) * 1.0 + 
                       Math.sin(idleT * 0.027) * 0.7 + 
                       Math.cos(idleT * 0.035) * 0.4;
        const jostleY = Math.sin(idleT * 0.011) * 0.6 + 
                       Math.cos(idleT * 0.017) * 0.4;

        x = baseX + jostleX;
        z = baseZ + jostleZ;
        y = baseY + jostleY;
        
        // Set camera Up to be North (negative Z) to orient correctly looking down
        up.set(0, 0, -1);
        break;
      }
      case IdleCameraMotion.MicroDollyBreathing:
      default: {
        const dz = Math.sin(idleT * 0.02) * 0.5;
        x = Math.cos(angleBase) * ORBIT_RADIUS;
        z = Math.sin(angleBase) * ORBIT_RADIUS + dz;
        y = 0;
        break;
      }
    }

    const pos = new THREE.Vector3(x, y, z);
    const target = new THREE.Vector3(0, 0, 0);

    return { pos, target, fov, distortionK: 0, up };
  }

  function applyPosNoise(now: number, base: CameraState): CameraState {
    // drift target
    if (Math.random() < 0.003) {
      noiseTarget.set(
        (Math.random() - 0.5) * NOISE_POS_AMPL,
        (Math.random() - 0.5) * NOISE_POS_AMPL,
        (Math.random() - 0.5) * NOISE_POS_AMPL
      );
    }

    noisePos.lerp(noiseTarget, NOISE_POS_LERP);

    const continuous = new THREE.Vector3(
      Math.sin(now * 0.15) * 0.8,
      Math.cos(now * 0.18) * 0.8,
      Math.sin(now * 0.16) * 0.8
    );

    const pos = base.pos.clone().add(noisePos).add(continuous);
    return { ...base, pos };
  }

  function applyMouseLook(now: number, base: CameraState): CameraState {
    const mouse = getMouseLookOffset();
    // small noise on look target
    const lookNoise = new THREE.Vector3(
      Math.sin(now * 0.12) * 1.2,
      Math.cos(now * 0.14) * 1.2,
      0
    );

    const target = base.target.clone()
      .add(new THREE.Vector3(mouse.x, mouse.y, 0))
      .add(lookNoise);

    return { ...base, target };
  }

  // intro overlays are defined as deltas around idle
  function applyIntro(now: number, base: CameraState): CameraState {
    if (!introType || introStart === null) return base;

    const t = clamp((now - introStart) / INTRO_DURATION, 0, 1);
    const w = easeInOutCubic(1 - t); // weight fades from 1 → 0

    if (t >= 1) {
      introType = null;
      introStart = null;
      return base;
    }

    let fovDelta = 0;
    let kDelta = 0;
    let radialScale = 0; // multiplicative radial change on distance to target
    let yOffset = 0; // vertical offset for dive effects
    let useDirectInterpolation = false; // for dive effects: interpolate directly from start to base

    const pos = base.pos.clone();
    const target = base.target.clone();
    const dir = pos.clone().sub(target);
    const dist = dir.length() || 1;
    dir.normalize();

    const focal = (fovDeg: number) => 1 / Math.tan(toRad(fovDeg) / 2);

    switch (introType) {
      case 'dolly-zoom': {
        // widen, then settle back
        const startFov = BASE_FOV + 25;
        const endFov = BASE_FOV - 15;
        const f = easeInOutCubic(t);
        const currentFov = startFov + (endFov - startFov) * f;
        fovDelta = (currentFov - base.fov) * w;

        // keep subject scale roughly constant
        const dNew = dist * (focal(currentFov) / focal(BASE_FOV));
        radialScale = (dNew - dist) * w;
        break;
      }
      case 'fov-ramp': {
        // burst wide then come back
        const peak = BASE_FOV + 55;
        const half = t < 0.5
          ? easeInOutCubic(t * 2)
          : easeInOutCubic((1 - t) * 2);
        const currentFov = BASE_FOV + (peak - BASE_FOV) * half;
        fovDelta = (currentFov - base.fov) * w;
        break;
      }
      case 'push-in-fov-expansion': {
        const fovMax = BASE_FOV + 40;
        const e = easeInOutCubic(t);
        const currentFov = BASE_FOV + (fovMax - BASE_FOV) * e;
        fovDelta = (currentFov - base.fov) * w;

        // push toward target ~35% of distance
        const push = dist * 0.35 * e;
        radialScale = -push * w; // move closer
        break;
      }
      case 'barrel-to-pincushion': {
        const e = easeInOutCubic(t);
        // start at barrel, fade to none (increased from -0.22 to -0.4 for more dramatic effect)
        kDelta = (-0.4 * (1 - e)) * w;
        // small fov swell
        const swell = Math.sin(Math.PI * e) * 6.0;
        fovDelta = swell * w;
        break;
      }
      case 'dive-up': {
        // Consciousness being uploaded: start far below, dive up toward center
        const e = easeInOutCubic(t);
        const startY = -80; // Start far below the scene
        // Interpolate directly from extreme position to base position
        useDirectInterpolation = true;
        yOffset = startY; // Store the extreme Y value
        // Barrel-to-pincushion distortion for immersion: start at barrel, fade to none (doubled for dramatic effect)
        kDelta = (-0.8 * (1 - e)) * w;
        // Slight FOV expansion as we approach, then settle
        const fovSwell = Math.sin(Math.PI * e) * 8.0;
        fovDelta = fovSwell * w;
        break;
      }
      case 'dive-down': {
        // Consciousness being downloaded: start far above, dive down toward center
        const e = easeInOutCubic(t);
        const startY = 80; // Start far above the scene
        // Interpolate directly from extreme position to base position
        useDirectInterpolation = true;
        yOffset = startY; // Store the extreme Y value
        // Barrel-to-pincushion distortion for immersion: start at barrel, fade to none (doubled for dramatic effect)
        kDelta = (-0.8 * (1 - e)) * w;
        // Slight FOV expansion as we approach, then settle
        const fovSwell = Math.sin(Math.PI * e) * 8.0;
        fovDelta = fovSwell * w;
        break;
      }
    }

    const newFov = base.fov + fovDelta;
    
    let newPos: THREE_NS.Vector3;
    if (useDirectInterpolation) {
      // For dive effects: interpolate directly from extreme starting position to base position
      // Create starting position at extreme Y, keeping X and Z from base
      const startPos = base.pos.clone();
      startPos.y = yOffset; // Start at extreme Y, but keep X and Z from base
      // Interpolate from extreme to base using eased t
      // The weight w is already accounted for in fovDelta and kDelta, so we just interpolate directly
      const e = easeInOutCubic(t);
      newPos = startPos.clone().lerp(base.pos, e);
      // Apply radial scale if needed (though dive effects don't use it)
      if (radialScale !== 0) {
        newPos.add(dir.multiplyScalar(radialScale * w));
      }
    } else {
      // For other effects: apply radial scale and Y offset as deltas
      newPos = pos.clone().add(dir.multiplyScalar(radialScale));
      newPos.y += yOffset; // Add vertical offset for other effects
    }

    return {
      pos: newPos,
      target: base.target,
      fov: newFov,
      distortionK: base.distortionK + kDelta,
      up: base.up,
    };
  }

  function applyNav(now: number, base: CameraState): CameraState {
    if (!navType || navStart === null) {
      // If we have a held state but no active nav, return held state
      if (heldNavState) {
        return heldNavState;
      }
      return base;
    }

    const t = clamp((now - navStart) / NAV_DURATION, 0, 1);
    const e = smoothstep(t);

    const pos = base.pos.clone();
    const target = base.target.clone();
    let fov = base.fov;

    switch (navType) {
      case NavCameraMotion.ToLogin: {
        // look slightly up, pull in, narrow fov
        const finalE = t >= 1 ? 1 : e; // Hold at final position after animation completes
        target.y += 1.8 * finalE;
        const dir = target.clone().sub(pos).normalize();
        pos.add(dir.multiplyScalar(ORBIT_RADIUS * 0.15 * finalE));
        fov = BASE_FOV - 4 * finalE;
        
        // When animation completes, save the held state and clear nav animation
        if (t >= 1) {
          heldNavState = {
            pos: pos.clone(),
            target: target.clone(),
            fov,
            distortionK: base.distortionK,
            up: base.up,
          };
          navType = null;
          navStart = null;
          return heldNavState;
        }
        break;
      }
      case NavCameraMotion.ToSettings: {
        // look down a bit, drop camera, widen fov
        const finalE = t >= 1 ? 1 : e; // Hold at final position after animation completes
        target.y -= 1.5 * finalE;
        pos.y -= 0.8 * finalE;
        fov = BASE_FOV + 6 * finalE;
        
        // When animation completes, save the held state and clear nav animation
        if (t >= 1) {
          heldNavState = {
            pos: pos.clone(),
            target: target.clone(),
            fov,
            distortionK: base.distortionK,
            up: base.up,
          };
          navType = null;
          navStart = null;
          return heldNavState;
        }
        break;
      }
      case NavCameraMotion.ToHome: {
        // Transition from held state back to base
        if (heldNavState) {
          // Interpolate from held state to base
          const returnE = 1 - e; // fade from held (1) to base (0)
          pos.copy(heldNavState.pos).lerp(base.pos, returnE);
          target.copy(heldNavState.target).lerp(base.target, returnE);
          fov = heldNavState.fov + (base.fov - heldNavState.fov) * returnE;
        } else {
          // No held state, just return base
          return base;
        }
        
        // When ToHome completes, clear everything
        if (t >= 1) {
          heldNavState = null;
          navType = null;
          navStart = null;
          return base;
        }
        break;
      }
    }

    // Only clear nav state for ToHome after it completes
    if (navType === NavCameraMotion.ToHome && t >= 1) {
      heldNavState = null;
      navType = null;
      navStart = null;
      return base;
    }

    return {
      pos,
      target,
      fov,
      distortionK: base.distortionK,
      up: base.up,
    };
  }

  function applyFlight(now: number, base: CameraState): CameraState {
    if (flightStart === null || !flightStartState || !flightTargetState) return base;

    const t = clamp((now - flightStart) / flightDuration, 0, 1);
    const e = easeInOutCubic(t);

    const pos = flightStartState.pos.clone();
    if (flightTargetState.pos) {
      pos.lerp(flightTargetState.pos, e);
    }

    const target = flightStartState.target.clone();
    if (flightTargetState.target) {
      target.lerp(flightTargetState.target, e);
    }

    let fov = flightStartState.fov;
    if (flightTargetState.fov !== undefined) {
      fov = fov + (flightTargetState.fov - fov) * e;
    }
    
    // When flight completes, we return the final state
    // BUT we don't clear it automatically because we want to hold the position
    // until another command comes in or we explicitly exit flight mode.
    // For now, let's just return the interpolated state.
    
    return {
      pos,
      target,
      fov,
      distortionK: base.distortionK, // Keep base distortion logic or interpolate if needed
      up: base.up, // Simple pass-through for now
    };
  }

  function smoothTowards(target: CameraState) {
    // fov
    smoothed.fov += (target.fov - smoothed.fov) * FOV_LERP;

    // distortion
    smoothed.distortionK +=
      (target.distortionK - smoothed.distortionK) * DISTORT_LERP;

    // position
    smoothed.pos.lerp(target.pos, POS_LERP);

    // target
    smoothed.target.lerp(target.target, TGT_LERP);

    // up
    smoothed.up.lerp(target.up, UP_LERP);

    // apply to real camera
    camera.fov = smoothed.fov;
    camera.position.copy(smoothed.pos);
    camera.up.copy(smoothed.up);
    camera.updateProjectionMatrix();
    camera.lookAt(smoothed.target);
  }

  return {
    update(nowSeconds: number) {
      if (!idleStart) idleStart = nowSeconds;
      const mode = getIdleMode();

      // 1) base idle
      let state = evalIdleBase(nowSeconds);

      // 2) noise (skip for Static)
      if (mode !== IdleCameraMotion.Static) {
        state = applyPosNoise(nowSeconds, state);
      }

      // 3) mouse look (skip for Static to ensure no movement)
      if (mode !== IdleCameraMotion.Static) {
        state = applyMouseLook(nowSeconds, state);
      }

      // 4) intro overlay
      state = applyIntro(nowSeconds, state);

      // 5) nav overlay
      state = applyNav(nowSeconds, state);

      // 6) flight overlay (overrides everything if active)
      state = applyFlight(nowSeconds, state);

      // 7) smooth into camera
      smoothTowards(state);

      return { distortionK: smoothed.distortionK };
    },

    startIntro(effect: IntroEffectType) {
      introType = effect;
      introStart = performance.now() * 0.001;
    },

    triggerNav(motion: NavCameraMotion) {
      // If transitioning to ToHome, keep the held state for transition
      // Otherwise, clear held state when starting a new nav animation
      if (motion !== NavCameraMotion.ToHome) {
        heldNavState = null;
      }
      // Cancel any active flight
      flightStart = null;
      
      navType = motion;
      navStart = performance.now() * 0.001;
    },

    flyTo(target: { pos?: THREE_NS.Vector3; lookAt?: THREE_NS.Vector3; fov?: number }, duration: number) {
      flightStart = performance.now() * 0.001;
      flightDuration = duration;
      
      // Capture current smoothed state as start point
      flightStartState = { ...smoothed, pos: smoothed.pos.clone(), target: smoothed.target.clone() };
      
      // Prepare target state
      flightTargetState = {
        pos: target.pos,
        target: target.lookAt,
        fov: target.fov
      };

      // Cancel other modes
      navType = null;
      heldNavState = null;
      introType = null;
    },

    getCurrentIntroType() {
      // Return null if intro is not active or has completed
      if (!introType || introStart === null) return null;
      const now = performance.now() * 0.001;
      const t = clamp((now - introStart) / INTRO_DURATION, 0, 1);
      if (t >= 1) return null; // Intro completed
      return introType;
    },
  };
}

